import React, { useMemo, useCallback, useState } from "react";
import { IntlShape, injectIntl, FormattedMessage } from "react-intl";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { Image } from "../shared/widgets/ImageManager";
import { getSchema } from "./schema";
import getTableColumns from "./tableColumns";
import { Link } from "react-router-dom";
import {
  DefaultTableRowActionsProps,
  Edit,
  StyledReactTooltip,
  Delete,
} from "../shared/actions/tableActions";
import { useHistory, useRouteMatch } from "react-router";
import {
  getFormConfiguration,
  getAllowedInventoryLines,
  getTravelWindowsFormConfiguration,
  getTravelWindowsTableColumns,
} from "./formConfiguration";
import { TableColumns } from "../shared/crud/list";
import { useConfig } from "../../lib/config";
import { SECTION } from "../../lib/constants";
import { FormConfiguration } from "../shared/crud/form";
import { usePropertyLocales } from "../../components/property-locale-provider";
import { usePropertyData } from "../../components/property-admin-data-provider";
import { RendererProps } from "../shared/renderers";
import { useGrpcClient, usePropertyGrpcCall } from "../../lib/grpc";
import { BundleAPIPromiseClient } from "../../gen/witbooking/backoffice/bundle/v1/bundle_api_grpc_web_pb";
import {
  Bundle,
  GetBundlesRequest,
  DeleteBundleRequest,
  Multimedia,
} from "../../gen/witbooking/backoffice/bundle/v1/bundle_api_pb";
import { messages } from "./messages";
import { Table } from "@devexpress/dx-react-grid-bootstrap4";
import SimpleCrud from "../shared/crud/simple-crud";
import styled from "styled-components";

import { Button } from "react-bootstrap";

const BundleContainer = styled.div`
  display: flex;
  & > div:first-child {
    margin-left: 20px;
    margin-top: 20px;
  }
`;

export interface CustomBundle extends Omit<Bundle.AsObject, "multimediaList"> {
  multimediaList: Image[];
}

export function CustomDateRenderer({ value = "" }: RendererProps<string>) {
  return value ? value.substring(0, 10) : null;
}

function multimediaFromCustomMultimedia(multimediaList: Multimedia.AsObject[]): Image[] {
  return multimediaList.map(multimedia =>
    Object.assign(new File([""], multimedia.filename), {
      id: multimedia.id,
      url: multimedia.url,
      filename: multimedia.filename,
      translations:
        (multimedia.translationsList &&
          multimedia.translationsList.length &&
          multimedia.translationsList.reduce((accum, curr) => {
            const locale = curr.locale;
            return {
              ...accum,
              [locale]: {
                title: curr.title,
                description: curr.description,
              },
            };
          }, {})) ||
        {},
    })
  );
}

function packageFromCustomPackage({ multimediaList, ...props }: Bundle.AsObject): CustomBundle {
  return {
    ...props,
    multimediaList: multimediaFromCustomMultimedia(multimediaList),
  } as CustomBundle;
}

function BundleCrud({ propertyTicker, intl }: { propertyTicker: string; intl: IntlShape }) {
  const config = useConfig();
  const match = useRouteMatch();
  const history = useHistory();
  const allowedLocales = usePropertyLocales();
  const propertyData = usePropertyData();

  // GRPC
  const grpcClient = useGrpcClient(BundleAPIPromiseClient);
  const getBundles = usePropertyGrpcCall(grpcClient, grpcClient.getBundles);
  const deleteBundle = usePropertyGrpcCall(grpcClient, grpcClient.deleteBundle);

  // TableColumns
  const tableColumns = useMemo<TableColumns<CustomBundle>>(() => getTableColumns(intl), [intl]);

  // Schema
  const customSchema = useMemo(() => getSchema(), []);

  // Form configuration
  const allowedInventoryLines = useMemo(() => getAllowedInventoryLines(propertyData), [
    propertyData,
  ]);
  const travelWindowsFormConfiguration = useMemo(() => getTravelWindowsFormConfiguration(), []);
  const travelWindowsTableColumns = useMemo(() => getTravelWindowsTableColumns(), []);
  const formConfiguration = useMemo<
    FormConfiguration<CustomBundle & { travelWindowSection?: string }>
  >(
    () =>
      getFormConfiguration(
        intl,
        allowedLocales,
        allowedInventoryLines,
        travelWindowsFormConfiguration,
        travelWindowsTableColumns,
        config
      ),
    [
      allowedLocales,
      allowedInventoryLines,
      intl,
      travelWindowsFormConfiguration,
      travelWindowsTableColumns,
      config,
    ]
  );

  // CRUD Methods
  const handleGetAll = useCallback(async (): Promise<CustomBundle[]> => {
    const request = new GetBundlesRequest();
    const response = await getBundles(request);
    const result = response.getBundleList().map(e => packageFromCustomPackage(e.toObject()));
    return result;
  }, [getBundles]);

  const [reload, setReload] = useState(true);
  const [forceUpdate, setForceUpdate] = useState(0);

  const handleDelete = useCallback(
    async (entityId: string) => {
      const request = new DeleteBundleRequest();
      request.setBundleId(entityId);
      await deleteBundle(request);
      setForceUpdate(forceUpdate + 1);
    },
    [deleteBundle, forceUpdate]
  );

  const handleViewHistory = useCallback(
    bundleId => {
      history.push(`/${propertyTicker}/bundles-edit/${bundleId}`);
    },
    [history, propertyTicker, match.url]
  );

  const TableRowActions = useCallback(
    ({ row, rest }: DefaultTableRowActionsProps<any>) => {
      return (
        <Table.Cell {...rest}>
          <StyledReactTooltip id="edit" effect="solid">
            <FormattedMessage {...messages.edit} />
          </StyledReactTooltip>
          <Edit onGoToEdit={() => handleViewHistory(row?.id)} row={row} />
          <Delete
            rest={rest}
            onDelete={handleDelete}
            row={row}
            onReloadList={() => {
              console.info("reload");
              setReload(reload);
            }}
          />
        </Table.Cell>
      );
    },
    [history, handleViewHistory, handleDelete]
  );

  const onGoToCreate = () => {
    history.push(`/${propertyTicker}/bundles-new`);
  };

  function BundleActions() {
    return (
      <Link className="btn btn-primary" to="bundles-new">
        <FormattedMessage {...messages.create} />
      </Link>
    );
  }

  return (
    <BundleContainer>
      <div>
        <SimpleCrud<CustomBundle & { travelWindowSection?: string }>
          actions={BundleActions}
          formConfiguration={formConfiguration}
          schema={customSchema}
          tableColumns={tableColumns}
          section={SECTION.BUNDLES}
          onGetAll={handleGetAll}
          onDelete={handleDelete}
          messages={messages}
          rowActions={props => <TableRowActions {...props} />}
          forceLoad={forceUpdate}
        />
      </div>
    </BundleContainer>
  );
}

export default injectIntl(withAuthorizationVerifier(BundleCrud));
